'use client';

import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  LabelDefault,
  MetaDefault,
  MonoDefault,
  MonoSmall,
  Tabs,
  getRoleDisplayName,
  ConfirmationDialog,
  toasterApi,
  CopyIdButton,
  WebpagesEndpointsTab,
  ASSET_TYPE_VALUES,
  Scroll,
  ArchivedLifecycleBadge,
  isArchivedLifecycle,
} from '@terra/ui-design-system';
import { Link02Icon, ComputerIcon } from '@terra/ui-icons';
import { AssetDetailsHeaderMutations } from '../common/AssetDetailsHeaderMutations';
import React, { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  getWebpageDetailsQueryKey,
  useWebpageDetails,
} from '../../hooks/api/useWebpageDetails';
import { TabScopeProvider, useTabScope } from '../../contexts/TabScopeContext';
import { SignalsContent } from './SignalsContent';
import { FindingsContent } from './FindingsContent';
import { TracesContent } from './TracesContent';
import { WebPageFeaturesContent } from './WebPageFeaturesContent';
import { WebPageDetailsSection } from './WebPageDetailsSection';
import { useWebPageTabs, useMainContentTabs } from '../../store/layoutStore';
import type { WebpageMainContentTab } from '../../store/layoutStore';
import { useAppTargetId } from '../../store/projectStore';

type WebPageDetailsTabProps = {
  tab: WebpageMainContentTab;
};
const ENTITY_TAB_TYPE = {
  ENDPOINT: 'endpoint',
  WEBPAGE: 'webpage',
} as const;
const safeGetHostname = (url?: string): string => {
  if (!url) return 'unknown';

  try {
    return new URL(url).hostname;
  } catch (error) {
    console.warn('Invalid URL for webpage:', url, error);

    return 'unknown';
  }
};

export function WebPageDetailsTab({ tab }: WebPageDetailsTabProps) {
  const appTargetId = useAppTargetId();
  const tAssetEntity = useTranslations('designSystem.signals.drawer');
  const tFeatures = useTranslations('webPageFeatures');
  const webpageId = tab.data.id;
  const parentTabScopeId = useTabScope();
  const {
    data: webpageData,
    isLoading,
    error,
  } = useWebpageDetails({ webpageId });
  const [selectedTab, setSelectedTab] = React.useState('details');
  const { selectedTab: storeSelectedTab, setTab } = useWebPageTabs(
    webpageId || 'default',
  );
  const activeTab = webpageId ? storeSelectedTab : selectedTab;
  const handleTabChange = webpageId ? setTab : setSelectedTab;
  const visibleActiveTab = activeTab;
  const webPageDetailPaneScopeId = React.useMemo(() => {
    if (!parentTabScopeId || !webpageId) {
      return undefined;
    }

    return `${parentTabScopeId}/subTab_${visibleActiveTab}`;
  }, [parentTabScopeId, webpageId, visibleActiveTab]);
  const { closeTab, navigateInActiveTab } = useMainContentTabs();
  const queryClient = useQueryClient();
  const tRelatedAssets = useTranslations(
    'designSystem.applicationAssets.drawer',
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleOpenRelatedEndpoint = useCallback(
    (relatedAsset: { id: string; type: string; title?: string }) => {
      if (relatedAsset.type !== ASSET_TYPE_VALUES.ENDPOINT) {
        return;
      }

      navigateInActiveTab({
        id: `${ENTITY_TAB_TYPE.ENDPOINT}-${relatedAsset.id}`,
        title: relatedAsset.title || tRelatedAssets('endpoint'),
        type: ENTITY_TAB_TYPE.ENDPOINT,
        data: { endpointId: relatedAsset.id },
      });
    },
    [navigateInActiveTab, tRelatedAssets],
  );
  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);
  const handleConfirmDelete = useCallback(async () => {
    if (!webpageId) return;
    setIsDeleting(true);

    try {
      await terraProxyApiClient.deleteWebPage(webpageId);
      toasterApi.success({
        title: 'Asset deleted',
        description: 'The web page has been deleted successfully.',
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['assets-unified'],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['asset-counts'],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['webPages'],
          exact: false,
        }),
      ]);
      setIsDeleteDialogOpen(false);
      closeTab(tab.id);
    } catch (error) {
      console.error('Failed to delete web page:', error);
      toasterApi.error({
        title: 'Failed to delete asset',
        description: 'There was an error deleting the web page.',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [webpageId, closeTab, tab.id, queryClient]);

  if (isLoading) {
    return (
      <Box h="full" display="flex" alignItems="center" justifyContent="center">
        <BodyDefault color="fg.muted">Loading webpage details...</BodyDefault>
      </Box>
    );
  }

  if (error || !webpageData) {
    return (
      <Box h="full" display="flex" alignItems="center" justifyContent="center">
        <VStack gap={3}>
          <BodyDefault color="fg.error">
            Failed to load webpage details
          </BodyDefault>
          <MetaDefault color="fg.muted">
            {error instanceof Error ? error.message : 'Unknown error'}
          </MetaDefault>
        </VStack>
      </Box>
    );
  }

  const hostname = safeGetHostname(webpageData.url);

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        <Box p="4" borderBottomWidth="1px" borderColor="border.subtle">
          <VStack gap="2" alignItems="flex-start" w="full">
            <HStack gap="3" alignItems="center" w="full">
              <Box
                display="flex"
                alignItems="center"
                flexShrink={0}
                aria-hidden
              >
                <ComputerIcon size={20} />
              </Box>
              <MonoDefault
                color="fg.default"
                flex="1"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {hostname}
              </MonoDefault>
              <ArchivedLifecycleBadge lifecycle={webpageData.lifecycle} />
              {webpageId && (
                <CopyIdButton
                  id={webpageId}
                  entityName={tAssetEntity('asset')}
                />
              )}
              <AssetDetailsHeaderMutations
                isArchived={isArchivedLifecycle(webpageData.lifecycle)}
                onDelete={handleDeleteClick}
              />
            </HStack>

            {webpageData.description && (
              <Box w="full">
                <BodyDefault color="fg.muted" maxW="full">
                  {webpageData.description}
                </BodyDefault>
              </Box>
            )}

            <HStack gap="2" alignItems="center" w="full" flex="1" minW="0">
              <Link02Icon size={14} aria-hidden style={{ flexShrink: 0 }} />
              <MonoSmall
                color="fg.muted"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {webpageData.url}
              </MonoSmall>
            </HStack>

            {webpageData.role && (
              <HStack gap="2" alignItems="center" w="full" flexWrap="wrap">
                <MetaDefault color="fg.muted">Role:</MetaDefault>
                <Badge size="sm" variant="outline">
                  {getRoleDisplayName(webpageData.role)}
                </Badge>
              </HStack>
            )}
          </VStack>
        </Box>

        <TabScopeProvider scopeId={webPageDetailPaneScopeId}>
          <Box flex="1" overflow="hidden">
            <Tabs.Root
              value={visibleActiveTab}
              size="sm"
              w="full"
              h="full"
              display="flex"
              flexDirection="column"
              overflow="hidden"
              lazyMount
              unmountOnExit
            >
              <Tabs.List justifyContent="flex-start">
                <Tabs.Trigger
                  value="details"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => handleTabChange('details')}
                >
                  <LabelDefault>Details</LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="signals"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => handleTabChange('signals')}
                >
                  <LabelDefault>Signals</LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="findings"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => handleTabChange('findings')}
                >
                  <LabelDefault>Findings</LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="executions"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => handleTabChange('executions')}
                >
                  <LabelDefault>Executions</LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="endpoints"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => handleTabChange('endpoints')}
                >
                  <LabelDefault>
                    {tRelatedAssets('tabs.endpoints')}
                  </LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="features"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => handleTabChange('features')}
                >
                  <LabelDefault>{tFeatures('tab')}</LabelDefault>
                </Tabs.Trigger>
                <Tabs.Indicator />
              </Tabs.List>

              <Box flex="1" overflow="hidden">
                <Tabs.Content value="details" h="full" pt="0">
                  <WebPageDetailsSection
                    webpageData={webpageData}
                    onScreenshotReplaced={async () => {
                      await queryClient.invalidateQueries({
                        queryKey: getWebpageDetailsQueryKey({
                          appTargetId,
                          webpageId,
                        }),
                      });
                    }}
                  />
                </Tabs.Content>

                <Tabs.Content value="signals" h="full" pt="0">
                  <SignalsContent
                    endpointId={webpageData.id}
                    endpointUrl={webpageData.url}
                    filterScope="endpoint"
                    lifecycleStateOverride={
                      isArchivedLifecycle(webpageData.lifecycle)
                        ? 'all'
                        : undefined
                    }
                  />
                </Tabs.Content>

                <Tabs.Content value="findings" h="full" pt="0">
                  <FindingsContent
                    endpointId={webpageData.id}
                    endpointUrl={webpageData.url}
                    lifecycleStateOverride={
                      isArchivedLifecycle(webpageData.lifecycle)
                        ? 'all'
                        : undefined
                    }
                  />
                </Tabs.Content>

                <Tabs.Content value="executions" h="full" pt="0">
                  <TracesContent
                    endpointUrl={webpageData.url}
                    lifecycleStateOverride={
                      isArchivedLifecycle(webpageData.lifecycle)
                        ? 'all'
                        : undefined
                    }
                  />
                </Tabs.Content>

                <Tabs.Content value="endpoints" h="full" pt="0">
                  <Scroll orientation="vertical" showScrollbar={true} h="full">
                    <Box p={4}>
                      <WebpagesEndpointsTab
                        webpageId={webpageData.id}
                        onOpenRelatedAsset={handleOpenRelatedEndpoint}
                      />
                    </Box>
                  </Scroll>
                </Tabs.Content>
                <Tabs.Content value="features" h="full" pt="0">
                  <WebPageFeaturesContent
                    webpageId={webpageData.id}
                    webpageScreenshotUrl={webpageData.signedUrl}
                  />
                </Tabs.Content>
              </Box>
            </Tabs.Root>
          </Box>
        </TabScopeProvider>
      </VStack>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={({ open }) => {
          setIsDeleteDialogOpen(open);
        }}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        variant="destructive"
        title="Delete Asset"
        description="Are you sure you want to delete this web page? This action cannot be undone."
      />
    </Box>
  );
}
